import type { RouteRecordRaw } from 'vue-router'

import type { DocType } from '@/constants/doctypes.ts'
import type { DoctypePermissionsMeta } from '@/types/internal/mainSidebarTypes.ts'

// stores
import { useAuthStore } from '@/stores/auth.ts'

const getRouteDoctypes = (route: RouteRecordRaw): DoctypePermissionsMeta | undefined => {
  if (route.meta?.doctypes) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return route.meta.doctypes as DoctypePermissionsMeta
  }

  for (const child of route.children ?? []) {
    const childDoctypes = getRouteDoctypes(child)
    if (childDoctypes) return childDoctypes
  }

  // eslint-disable-next-line @typescript-eslint/consistent-return
  return undefined
}

const hasAccess = (dts?: DoctypePermissionsMeta, if_owner: boolean | null = null): boolean => {
  if (!dts) return true
  const authStore = useAuthStore()
  return Object.entries(dts).every(([doctype, requiredPerms]) =>
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    authStore.hasOnePermission(doctype as DocType, requiredPerms, false, if_owner),
  )
}

const hasAccessToRoute = (route: RouteRecordRaw): boolean =>
  hasAccess(getRouteDoctypes(route), route.meta?.if_owner ?? null)

const resolveMenuLink = (
  mainLink: RouteRecordRaw,
  subRoutes?: RouteRecordRaw[],
): null | RouteRecordRaw => {
  const mainLinkDoctypes = getRouteDoctypes(mainLink)
  const hasMainLinkPermissionMeta = Boolean(mainLinkDoctypes)

  // Main link has explicit permission metadata and is accessible.
  if (hasMainLinkPermissionMeta && hasAccessToRoute(mainLink)) return mainLink

  // Fallback to sub routes when main link is inaccessible or has no own permission metadata.
  if (subRoutes) {
    for (const sub of subRoutes) {
      const subDoctypes = getRouteDoctypes(sub)
      if (hasAccess(subDoctypes, sub.meta?.if_owner ?? null)) return sub
    }
  }

  // Keep main link only if it has no sub routes and no permission metadata to evaluate.
  if (!hasMainLinkPermissionMeta && !subRoutes?.length) {
    return mainLink
  }

  return null
}

export { getRouteDoctypes, hasAccess, hasAccessToRoute, resolveMenuLink }
